import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  markCampaignVideoPosted,
  submitCampaignVideo,
} from "../../../services/creator/campaignApiFn";
import { baseUrl } from "../../../services/index";
import { handleOpenSnackBar } from "../../../utils";

const MAX_VIDEO_DURATION = 60; // seconds
const MAX_VIDEO_SIZE = 200 * 1024 * 1024; // 200MB
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];

export const submissionStatusColor = {
  submitted: "warning",
  approved: "info",
  rejected: "error",
  posted: "success",
};

// Browser me hi video ki length nikalte hain (upload se pehle)
const getVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Unable to read video"));
    };
    video.src = url;
  });

const isValidUrl = (value) => /^https?:\/\/\S+\.\S+/.test(value.trim());

function CampaignVideoSubmission({ campaign, creatorId, onUpdated }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const submission = campaign?.participation?.submission;
  const platforms = (campaign?.kpis || []).map((kpi) => kpi.platform);

  const [selectedVideo, setSelectedVideo] = useState(null); // { file, previewUrl, duration }
  const [caption, setCaption] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingLinks, setIsEditingLinks] = useState(false);
  const [postLinks, setPostLinks] = useState({});
  const [linkErrors, setLinkErrors] = useState({});

  useEffect(() => {
    const links = {};
    (submission?.posts || []).forEach((post) => {
      links[post.platform] = post.url;
    });
    setPostLinks(links);
  }, [submission?.posts]);

  // Local preview URL free karna
  useEffect(() => {
    return () => {
      if (selectedVideo?.previewUrl) URL.revokeObjectURL(selectedVideo.previewUrl);
    };
  }, [selectedVideo]);

  const handleSelectVideo = async (event) => {
    const file = event.target.files[0];
    fileInputRef.current.value = null;
    if (!file) return;

    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      return handleOpenSnackBar("error", t("videoFormatNotAllowed"));
    }
    if (file.size > MAX_VIDEO_SIZE) {
      return handleOpenSnackBar("error", t("videoSizeLimit"));
    }

    try {
      const duration = await getVideoDuration(file);
      if (!duration || Math.round(duration) > MAX_VIDEO_DURATION) {
        return handleOpenSnackBar("error", t("videoDurationLimit"));
      }
      setSelectedVideo({
        file,
        duration: Math.round(duration),
        previewUrl: URL.createObjectURL(file),
      });
    } catch (error) {
      handleOpenSnackBar("error", t("videoReadFailed"));
    }
  };

  const handleSubmitVideo = async () => {
    if (!selectedVideo) return;
    try {
      setIsSubmitting(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append("video", selectedVideo.file);
      const uploadResponse = await axios({
        method: "post",
        url: `${baseUrl}campaign-video/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(
              Math.round((progressEvent.loaded * 100) / progressEvent.total),
            );
          }
        },
      });
      if (!uploadResponse?.data?.url) throw new Error(t("videoUploadFailed"));

      const response = await submitCampaignVideo(creatorId, campaign._id, {
        video: {
          url: uploadResponse.data.url,
          name: selectedVideo.file.name,
          size: selectedVideo.file.size,
          duration: selectedVideo.duration,
        },
        caption: caption.trim(),
      });

      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", t("videoSubmittedSuccess"));
        setSelectedVideo(null);
        setCaption("");
        onUpdated?.();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message || t("videoUploadFailed"));
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  const handleMarkPosted = async () => {
    const errors = {};
    const posts = [];
    platforms.forEach((platform) => {
      const url = (postLinks[platform] || "").trim();
      if (!url) return;
      if (!isValidUrl(url)) errors[platform] = t("invalidPostLink");
      else posts.push({ platform, url });
    });
    setLinkErrors(errors);
    if (Object.keys(errors).length) return;
    if (!posts.length) {
      return handleOpenSnackBar("error", t("atLeastOnePostLink"));
    }

    try {
      setIsSubmitting(true);
      const response = await markCampaignVideoPosted(creatorId, campaign._id, {
        posts,
      });
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", t("videoMarkedPosted"));
        setIsEditingLinks(false);
        onUpdated?.();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canUpload = !submission || submission.status === "rejected";
  const isCampaignClosed = campaign?.status !== "published";

  const renderPlayer = (src) => (
    <Box
      component="video"
      src={src}
      controls
      playsInline
      sx={{
        width: "100%",
        maxWidth: 420,
        maxHeight: 520,
        bgcolor: "black",
        borderRadius: 2,
      }}
    />
  );

  const renderUploadSection = () => {
    if (isCampaignClosed) {
      return <Alert severity="info">{t("campaignClosedNoSubmission")}</Alert>;
    }

    return (
      <Stack gap={1.5}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_VIDEO_TYPES.join(",")}
          hidden
          onChange={handleSelectVideo}
        />

        {selectedVideo ? (
          <>
            {renderPlayer(selectedVideo.previewUrl)}
            <Typography variant="body2" color="text.secondary">
              {selectedVideo.file.name} ·{" "}
              {t("secondsCount", { count: selectedVideo.duration })} ·{" "}
              {(selectedVideo.file.size / (1024 * 1024)).toFixed(1)} MB
            </Typography>
            <TextField
              label={t("captionOptional")}
              multiline
              minRows={2}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              disabled={isSubmitting}
              fullWidth
            />
            {uploadProgress !== null && (
              <Stack gap={0.5}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  {t("uploadingVideo", { progress: uploadProgress })}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" gap={1}>
              <Button
                variant="outlined"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo(null)}
              >
                {t("changeVideo")}
              </Button>
              <Button
                variant="contained"
                disabled={isSubmitting}
                startIcon={
                  isSubmitting && <CircularProgress size={14} color="inherit" />
                }
                onClick={handleSubmitVideo}
              >
                {t("submitVideo")}
              </Button>
            </Stack>
          </>
        ) : (
          <Button
            variant="outlined"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => fileInputRef.current.click()}
            sx={{ borderStyle: "dashed", py: 2, maxWidth: 420 }}
          >
            {t("uploadVideo")}
          </Button>
        )}
        <Typography variant="caption" color="text.secondary">
          {t("videoRequirementsNote")}
        </Typography>
      </Stack>
    );
  };

  const renderPostLinksForm = () => (
    <Stack gap={1.5} maxWidth={520}>
      {platforms.map((platform) => (
        <TextField
          key={platform}
          size="small"
          label={t("postLinkFor", { platform: t(platform) })}
          placeholder="https://"
          value={postLinks[platform] || ""}
          error={Boolean(linkErrors[platform])}
          helperText={linkErrors[platform]}
          onChange={(e) => {
            setLinkErrors((prev) => ({ ...prev, [platform]: null }));
            setPostLinks((prev) => ({ ...prev, [platform]: e.target.value }));
          }}
          disabled={isSubmitting}
          fullWidth
        />
      ))}
      <Stack direction="row" gap={1}>
        {submission?.status === "posted" && (
          <Button
            variant="outlined"
            disabled={isSubmitting}
            onClick={() => setIsEditingLinks(false)}
          >
            {t("cancel")}
          </Button>
        )}
        <Button
          variant="contained"
          disabled={isSubmitting}
          startIcon={
            isSubmitting && <CircularProgress size={14} color="inherit" />
          }
          onClick={handleMarkPosted}
        >
          {t("markAsPosted")}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Stack gap={2}>
      {submission && (
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Chip
            size="small"
            color={submissionStatusColor[submission.status]}
            label={t(`videoStatus_${submission.status}`)}
          />
          <Typography variant="body2" color="text.secondary">
            {t("submittedOn")}:{" "}
            {moment(submission.submittedAt).format("DD/MM/YYYY hh:mm a")}
          </Typography>
        </Stack>
      )}

      {submission?.status === "rejected" && (
        <Alert severity="error">
          {t("videoRejectedMessage")}
          {submission.rejectionReason && (
            <>
              <br />
              <b>{t("reason")}:</b> {submission.rejectionReason}
            </>
          )}
        </Alert>
      )}

      {canUpload ? (
        renderUploadSection()
      ) : (
        <>
          {submission.status === "submitted" && (
            <Alert severity="warning">{t("videoUnderReviewMessage")}</Alert>
          )}
          {submission.status === "approved" && (
            <Alert severity="success">{t("videoApprovedMessage")}</Alert>
          )}

          {renderPlayer(submission.video?.url)}

          {submission.caption && (
            <Typography
              variant="body2"
              color="text.secondary"
              whiteSpace="pre-line"
            >
              {submission.caption}
            </Typography>
          )}

          {submission.status === "approved" && renderPostLinksForm()}

          {submission.status === "posted" &&
            (isEditingLinks ? (
              renderPostLinksForm()
            ) : (
              <Stack gap={1}>
                <Typography variant="body2" fontWeight={600}>
                  {t("postedLinks")}
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                  {(submission.posts || []).map((post) => (
                    <Chip
                      key={post.platform}
                      label={t(post.platform)}
                      icon={<OpenInNewIcon fontSize="small" />}
                      component="a"
                      href={post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      variant="outlined"
                    />
                  ))}
                  <Button size="small" onClick={() => setIsEditingLinks(true)}>
                    {t("editLinks")}
                  </Button>
                </Stack>
              </Stack>
            ))}
        </>
      )}
    </Stack>
  );
}

export default CampaignVideoSubmission;
